import SaveIcon from '@mui/icons-material/Save';
import { Box, Card, CardActions, CardHeader, Divider, Grid, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
    SelectElement,
    SwitchElement,
    TextareaAutosizeElement,
    useWatch,
} from 'react-hook-form-mui';
import { makeStyles } from 'tss-react/mui';
import { settingsRoutes } from '../../../routes';
import { EditUserTwilioPhoneData } from '../../../sic-lib/types/EditUserTwilioPhoneData';
import NavLinkButton from '../../Navigation/NavLinkButton/NavLinkButton';
import AxiosErrorAlert from '../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../Shared/ButtonLoading';
import DisplayPhoneNumber from '../../Shared/DisplayPhoneNumber/DisplayPhoneNumber';
import PhoneNumberTextFieldElement from '../../Shared/FormControls/Inputs/PhoneNumberTextFieldElement';

type Props = {
    phoneSettings: EditUserTwilioPhoneData;
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
    error: any;
};

const forwardAfterOptions = [
    {
        id: 11,
        label: '11 seconds. (approx. 3 rings)',
    },
    {
        id: 17,
        label: '17 seconds. (approx. 4 rings)',
    },
    {
        id: 23,
        label: '23 seconds. (approx. 5 rings)',
    },
];

export function PhoneSettingsFormControls({
    phoneSettings,
    isLoading,
    isSuccess,
    isError,
    error,
}: Props) {
    const { classes } = useStyles();

    const isForwardEnabled = useWatch({
        name: 'forwardEnabled',
    }) as EditUserTwilioPhoneData['forwardEnabled'];

    return (
        <Card className={classes.root}>
            <CardHeader title="StayInContact Phone Settings" />
            <Divider />
            <Grid container spacing={2}>
                <Grid
                    item
                    md={6}
                    sm={12}
                    xs={12}
                    display="flex"
                    flexDirection="column"
                    alignContent="center"
                    justifyContent="center"
                >
                    <Box p={2} className={classes.phoneWrap}>
                        <Typography variant="subtitle1">My StayInContact Phone Number</Typography>
                        <Typography variant="h6">
                            <DisplayPhoneNumber phoneNumber={phoneSettings.number} />
                        </Typography>
                        <NavLinkButton pathname={settingsRoutes.changeSicNumber}>
                            Change my StayInContact Phone Number
                        </NavLinkButton>
                    </Box>
                </Grid>
                <Grid item md={6} sm={12} xs={12}>
                    <Box p={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <SwitchElement
                                    name="forwardEnabled"
                                    label="Forward calls after no answer"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <PhoneNumberTextFieldElement
                                    name="forwardNumber"
                                    label="Forward to"
                                    disabled={!isForwardEnabled}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <SelectElement
                                    name="forwardDelay"
                                    label="Forward After"
                                    disabled={!isForwardEnabled}
                                    options={forwardAfterOptions}
                                    fullWidth
                                    validation={{
                                        min: {
                                            value: 4,
                                            message: 'Please select a valid option.',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextareaAutosizeElement
                                    name="inboundCallMessage"
                                    label="Inbound Call Pre-Connect Message"
                                    fullWidth
                                    multiline={true}
                                    helperText="Say this message to a user when they call you before ringing your phone. Commonly used to inform a caller that their call will be recorded. Leave this empty to disable this feature."
                                    validation={{
                                        maxLength: {
                                            value: 120,
                                            message: 'This message is too long.',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <AxiosErrorAlert error={error} />
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
            <Divider />
            <CardActions>
                <ButtonLoading
                    variant="contained"
                    color="primary"
                    type="submit"
                    isLoading={isLoading}
                    disabled={isLoading}
                    isError={isError}
                    isSuccess={isSuccess}
                    startIcon={<SaveIcon />}
                >
                    Save
                </ButtonLoading>
            </CardActions>
        </Card>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        marginBottom: theme.spacing(2),
    },
    header: {
        position: 'relative',
        padding: theme.spacing(2),
        '& > h3': {
            textTransform: 'capitalize',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            display: 'block',
            width: '100%',
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
    },
    list: {
        padding: theme.spacing(2),
    },
    linkText: {
        //color: theme.palette.common.white
    },
    error: {
        color: 'red',
        padding: theme.spacing(2),
    },
    phoneWrap: {
        textAlign: 'center',
    },
}));
